/** A filing's disclosure lines, and the editors and receipts that hang off them.
 *
 * Moved out of `Reports.tsx` unchanged when the screen was split into a list of filings and a
 * filing with tabs: the lines are the Disclosures tab, and nothing about how a line is drawn
 * changed. The comments are the original ones and still describe what they sit on.
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";

import type { ReportLine } from "../api/queries";
import { useReloadReportLine, useWriteStatement } from "../api/queries";
import { Refused } from "../api/refusal";
import { useMe } from "../api/auth";
import { WriteFailed } from "../components/Loaded";
import { shortDate } from "../lib/format";
import { keepSearch } from "./search";
import own from "./Reports.module.css";
import { FactorLimitation } from "./filings/FactorLimitation";
import { amount, cls, receiptIdsOf } from "./reportsFormat";
import { customerText } from "../lib/customerWords";

/** The roles `PUT /reports/{regime}/statements/{disclosure}` accepts, mirrored from `reports.py`'s
 * `MAY_WRITE` (F-005).
 *
 * **A mirror and not an authority** — the server refuses; this only decides what to render. A list
 * rather than `role !== "viewer"` so a sixth role added to the API defaults to *not shown the
 * editor* instead of silently inheriting one. Narrower than `MAY_READ` on the same file, which is
 * the whole of the finding: every role could read a statement and the editor was drawn for all five.
 */
const MAY_WRITE: readonly string[] = ["admin", "approver", "contributor"];

/** What a statement line reads, for the sentence that says someone else changed it. A paragraph is
 * quoted (cut at 160 characters: the full text is on the line above the box), a ratio is its figure,
 * and a line with nothing on it says so. */
function nowReadsOfLine(line: ReportLine): string {
  if (line.value === null) return "nothing written yet";
  if (line.measure === "intensity") return `${amount(line.value, line.unit)} ${line.unit ?? ""}`.trim();
  const words = line.value.length > 160 ? `${line.value.slice(0, 160)}\u2026` : line.value;
  return `\u201c${words}\u201d`;
}

/** Where the company writes what only it can say.
 *
 * Three of SECR's requirements come from nowhere else: the principal energy-efficiency measures
 * taken (20D(4)), the methodologies used (20F), and the quantifiable factor an intensity ratio is
 * expressed against (20G). The report blocks on all three until they exist, so this is the field
 * that turns "cannot be filed" into "can".
 *
 * The server's refusal is **rendered**, not swallowed. An empty statement is refused — a row that
 * exists and says nothing satisfies the check that asks whether it exists — and a person who
 * cleared the box needs to see why the save did not take rather than watching it silently revert.
 */
export function StatementEditor({
  line,
  regimeKey,
  obligationId,
  method,
  filingWindow,
}: {
  line: ReportLine;
  regimeKey: string;
  obligationId: string | undefined;
  method: "location" | "market";
  //: The **filing's** period, from the report's own context — not the period picker at the top of
  //: the app. A methodologies statement belongs to the year being filed, and a group whose
  //: subsidiary has a March year end has two different ones open at once (F11).
  filingWindow: { period_start: string; period_end: string };
}) {
  const isRatio = line.measure === "intensity";
  const [text, setText] = useState("");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const write = useWriteStatement(regimeKey, obligationId, method, filingWindow);
  const reloadLine = useReloadReportLine(obligationId, method);
  // **The version of the statement this editor was opened with** (G-P9-04): `0` for one not yet made.
  // Held here, and moved only by this editor's own save or a re-apply, because the report refetches in
  // the background and a version read off the refetched line would make the check compare the row
  // with itself.
  const [openedVersion, setOpenedVersion] = useState(line.statement_version ?? 0);
  // The line as a fresh read has it, once a save has been refused as stale.
  const [fresh, setFresh] = useState<ReportLine | null>(null);
  const [reapplying, setReapplying] = useState(false);
  const { data: me } = useMe();
  //: `undefined` while `/auth/me` is in flight, which is **not** "may not write": a sentence flashed
  //: at every Contributor on every load would teach them to ignore it. The editor is drawn disabled
  //: until the answer is known, and nothing is asserted in the meantime (`Bulk.tsx`).
  const mayWrite = me ? MAY_WRITE.includes(me.role) : undefined;

  /** Put the editor back as it was. A statement typed into one disclosure and abandoned is not a
   * draft anybody chose to keep, and it is the company's own words — the field most dangerous to
   * leave lying in another line's editor. */
  const clear = () => {
    setText("");
    setValue("");
    setLabel("");
    write.reset();
    // Starting over is opening the editor again: it is on the statement as the report has it now.
    setOpenedVersion(line.statement_version ?? 0);
    setFresh(null);
  };

  const send = (expectedVersion: number) => {
    setFresh(null);
    return write
      .mutateAsync(
        isRatio
          ? { disclosureKey: line.key, denominator_value: value, denominator_label: label, expectedVersion }
          : { disclosureKey: line.key, statement: text, expectedVersion },
      )
      .then(
        (saved) => setOpenedVersion(saved.version ?? expectedVersion),
        (error: unknown) => {
          // Said beside the editor with what the statement now reads; nothing typed is touched.
          if (error instanceof Refused && error.isStale) void reloadLine(line.key).then((read) => setFresh(read ?? null));
        },
      );
  };

  const submit = () => void send(openedVersion);

  const reapply = async () => {
    setReapplying(true);
    try {
      const read = await reloadLine(line.key);
      if (!read) return;
      const version = read.statement_version ?? 0;
      setOpenedVersion(version);
      await send(version);
    } finally {
      setReapplying(false);
    }
  };

  //: **A Viewer or an Auditor was shown the box and Save** and collected a 403 after typing a
  //: paragraph — the company's own words, which is the one input here that is expensive to lose
  //: (F-005, design rule 6). Told instead, with who can. The statement already on the line, if any,
  //: is rendered by the caller above, so reading it is untouched.
  if (mayWrite === false) {
    return (
      <span className={cls(own.editor)}>
        <span className={cls(own.saveNote)} data-testid="statement-who-can">
          An <strong>Admin</strong>, an <strong>Approver</strong> or a <strong>Contributor</strong>{" "}
          writes this statement. It is the company&rsquo;s own words, and every change to it is
          recorded in the ledger.
        </span>
      </span>
    );
  }

  return (
    <span className={cls(own.editor)} data-testid="statement-editor">
      {isRatio ? (
        <span className={cls(own.ratio)}>
          <input
            type="text"
            inputMode="decimal"
            aria-label={`Denominator for ${line.label}`}
            disabled={mayWrite === undefined}
            placeholder="18400"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <input
            type="text"
            aria-label={`What the denominator measures for ${line.label}`}
            disabled={mayWrite === undefined}
            placeholder="tonnes of product"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </span>
      ) : (
        <textarea
          rows={3}
          aria-label={line.label}
          disabled={mayWrite === undefined}
          placeholder="The company's own words."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      )}
      <span className={cls(own.editorRow)}>
        <button
          type="button"
          className={cls(own.save)}
          onClick={submit}
          disabled={write.isPending || mayWrite === undefined}
        >
          {write.isPending ? "Saving…" : "Save"}
        </button>
        {/* Cancel, once there is something to cancel. */}
        {text || value || label ? (
          <button
            type="button"
            className={cls(own.save)}
            data-testid="cancel-statement"
            onClick={clear}
          >
            Cancel
          </button>
        ) : null}
        {write.isError ? (
          /* **No `retrySafe`, and not for the reason this comment used to give.**
             It said *"a re-sent statement is a second version rather than the same one"*, and that
             is false: `statements.write` compares `previous == _snapshot(row)` and returns
             **before** it bumps the version, before the `DisclosureStatementVersion` insert and
             before `ledger.append`, with its own comment saying why — *saving a form without
             changing anything is not a version*. So a re-sent identical statement already writes
             nothing, and a false sentence in a comment is what the next reader reasons from.

             The honest reason to withhold it is the one that is still true: the route **cannot
             say** which happened. It answers the same `200` whether it wrote a version or
             recognised a no-op, so the client cannot distinguish them — and `retrySafe` prints a
             claim about what the server did, not about what it wrote. A re-send of a statement the
             reader has since *edited* is also a real second version, which a timeout gives them no
             way to know. Until the response distinguishes the two, the honest sentence is that the
             outcome is unknown and the reader should look. */
          <WriteFailed
            error={write.error}
            of="this statement"
            className={cls(own.saveError)}
            onRetry={submit}
            nowReads={fresh ? nowReadsOfLine(fresh) : undefined}
            onReapply={() => void reapply()}
            reapplying={reapplying}
          />
        ) : (
          <span className={cls(own.saveNote)}>
            This is the company&rsquo;s own statement. Every change is recorded in the ledger.
          </span>
        )}
      </span>
    </span>
  );
}

/** Which records this figure **cut**, and the fraction that cut each one — rule 3, EXP-36.
 *
 * A line that is partly a December bill was resolvable before this and not complete: a reader could
 * open the record and find a whole month's gas while the line in front of them said less, and
 * nothing in between named the days. The allocator computed the fraction and the fold threw it
 * away.
 *
 * **Shown only where there is one**, which is a minority of lines. A panel that appeared on every
 * line saying "0 apportioned" is a panel people stop reading — the same reason the data-quality
 * note is silent at 0%.
 *
 * **Days, not a percentage.** `13 of 31 days` is the arithmetic; `41.9%` is a rounding of it, and a
 * reader checking a filed figure wants the thing they can divide. And each one **opens its own
 * receipt**, because a fraction attached to a record nobody can reach is a worse receipt than no
 * fraction at all.
 */
function Apportioned({ line }: { line: ReportLine }) {
  const [open, setOpen] = useState(false);
  const cut = line.apportioned;
  return (
    <span className={cls(own.apportioned)}>
      <button
        type="button"
        className={cls(own.apportionedToggle)}
        aria-expanded={open}
        data-testid="apportioned-toggle"
        onClick={() => setOpen((was) => !was)}
      >
        {open ? "Hide" : "Show"} the {cut.length} record{cut.length === 1 ? "" : "s"} this figure
        apportions
      </button>
      {open ? (
        <ul className={cls(own.apportionedList)} data-testid="apportioned">
          {cut.map((slice) => (
            <li key={slice.calculation_id}>
              <Link
                to="/reports"
                search={(prev: Record<string, unknown>) => ({
                  ...keepSearch(prev),
                  tab: typeof prev.tab === "string" ? prev.tab : undefined,
                  receipt: slice.calculation_id,
                })}
              >
                {shortDate(slice.record_start)} – {shortDate(slice.record_end)}
              </Link>{" "}
              · {slice.covered_days} of {slice.total_days} days ·{" "}
              <strong>{amount(slice.amount, "tCO2e")}</strong> of{" "}
              {amount(slice.full_amount, "tCO2e")} tCO₂e
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}

/** The register, narrowed to the entity and period a filing is about - **rule 3 for a report line**.
 *
 * A line's figure is a sum over calculations. `ReportLineOut` now lists their ids (`receipt_ids`,
 * capped, with `receipt_ids_truncated`), and `LineReceipts` opens them through the receipt drawer.
 * This link remains for what a line cannot open itself: a line from an API that predates the field,
 * a capped list, and the unevidenced total. What it does without inventing a selector is send the
 * reader to the register for this company and this period, where every figure is a row and every
 * row opens its receipt. That is a *superset* of any one line, and the copy beside the link says so.
 *
 * The line-to-calculations mapping lives in the backend (`DisclosureSpec.categories`, `carriers`,
 * `jurisdiction`); re-deriving it here would be a second home for a regulatory selector.
 */
export function FiguresInRegister({
  report,
  status,
  testId,
  children,
}: {
  report: { scope2_method: string; period_start: string; period_end: string; context: { legal_entity_name: string } };
  /** `broken` narrows to the figures whose evidence chain is incomplete. */
  status?: "broken";
  testId: string;
  children: ReactNode;
}) {
  return (
    <Link
      to="/emissions"
      search={{
        method: report.scope2_method === "market" ? "market" : "location",
        from: report.period_start,
        to: report.period_end,
        entity: report.context.legal_entity_name,
        tab: "register",
        ...(status ? { status } : {}),
      }}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}


/** The calculations behind a report line, each opening **the existing receipt drawer** - rule 3.
 *
 * A link here is `?receipt=<calculation id>`, the same address `Apportioned` and the register use,
 * so the drawer (`ReceiptDrawer`, one rendering of a chain) is the only place a chain is drawn. This
 * component prints no part of a receipt.
 *
 * **A capped list says so.** The server lists at most 200 ids while `records` is the true count, so
 * when `receipt_ids_truncated` is set the list is a prefix and the sentence names both numbers; a
 * list that looked complete would be a figure whose missing receipts nobody knew to look for. The
 * register link beside it reaches every calculation for the period.
 */
function LineReceipts({
  line,
  entityName,
  method,
  filingWindow,
}: {
  line: ReportLine;
  entityName: string;
  method: "location" | "market";
  filingWindow: { period_start: string; period_end: string };
}) {
  const [open, setOpen] = useState(false);
  const { ids, truncated } = receiptIdsOf(line);
  if (ids.length === 0) return null;
  return (
    <span className={cls(own.apportioned)}>
      <button
        type="button"
        className={cls(own.apportionedToggle)}
        aria-expanded={open}
        data-testid="line-receipts-toggle"
        onClick={() => setOpen((was) => !was)}
      >
        {open ? "Hide" : "Show"} the receipt{ids.length === 1 ? "" : "s"} behind this figure
      </button>
      {open ? (
        <span data-testid="line-receipts">
          <ul className={cls(own.apportionedList)}>
            {ids.map((id, index) => (
              <li key={id}>
                <Link
                  to="/reports"
                  search={(prev: Record<string, unknown>) => ({ ...keepSearch(prev), tab: typeof prev.tab === "string" ? prev.tab : undefined, receipt: id })}
                  data-testid="line-receipt-link"
                >
                  Receipt {index + 1}
                </Link>
              </li>
            ))}
          </ul>
          {truncated ? (
            <span data-testid="line-receipts-truncated">
              Only the first {ids.length} of {line.records} calculations are listed here.{" "}
              <FiguresInRegister
                report={{
                  scope2_method: method,
                  period_start: filingWindow.period_start,
                  period_end: filingWindow.period_end,
                  context: { legal_entity_name: entityName },
                }}
                testId="line-receipts-register"
              >
                Open the register for every one
              </FiguresInRegister>
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export function Line({
  line,
  regimeKey,
  obligationId,
  method,
  filingWindow,
  entityName,
}: {
  line: ReportLine;
  regimeKey: string;
  obligationId: string | undefined;
  method: "location" | "market";
  filingWindow: { period_start: string; period_end: string };
  entityName: string;
}) {
  // Prose, not a figure: a narrative answers its requirement in words, and there is nothing to put
  // in a right-hand column. An intensity ratio is a number and belongs there like the rest.
  const isProse = line.measure === "narrative";
  // A disclosure EmitIQ cannot produce is not one a text box makes producible — SB 253's scope 3
  // and SB 261's climate-risk report are whole reports, not statements.
  const isUnsupported =
    line.unavailable_because === "EmitIQ does not produce this disclosure. See the limitation.";

  return (
    <div className={cls(own.line, isUnsupported && own.lineUnsupported)} data-testid={`report-line-${line.key}`} data-disclosure-key={line.key}>
      <span className={cls(own.body)}>
        <span className={cls(own.label)}>{line.label}</span>
        {/* The regulation's own words, set apart from our heading. A reader has to be able to see
            which is which without being told which is which. */}
        <span className={cls(own.requirement)}>{line.requirement}</span>
        <span className={cls(own.cite)}>
          {line.source_url ? (
            <a href={line.source_url} target="_blank" rel="noreferrer">
              {customerText(line.citation)}
            </a>
          ) : (
            customerText(line.citation)
          )}
          {line.records > 0 ? ` · ${line.records} calculation${line.records === 1 ? "" : "s"}` : ""}
        </span>
        {line.basis === "guidance" ? (
          <span className={cls(own.guidance)}>Guidance, not the paragraph</span>
        ) : null}
        {line.apportioned.length > 0 ? <Apportioned line={line} /> : null}
        <FactorLimitation line={line} />
        <LineReceipts
          line={line}
          entityName={entityName}
          method={method}
          filingWindow={filingWindow}
        />
        {/* A figure built from calculations that lists none of them says why it has no receipts
            of its own, rather than leaving a count with nothing behind it. */}
        {line.value !== null && line.records > 0 && receiptIdsOf(line).ids.length === 0 ? (
          <span className={cls(own.limitation)} data-testid="line-receipts-unlisted">
            This line does not list its calculations, so it does not open receipts itself. Every one
            is in the register for this company and period.
          </span>
        ) : null}
        {line.limitation ? <span className={cls(own.limitation)}>{line.limitation}</span> : null}
        {/* Only the lines the company itself supplies. A figure computed from the inventory is not
            something anyone types over, and an unsupported one (SB 253's scope 3) is not something
            a text box makes producible. */}
        {/* What the company has said so far, where it has said anything. In the body because it is
            prose, and above the box rather than inside it so rewriting starts from a blank field
            and an unsaved edit can never be mistaken for the statement of record. */}
        {isProse && line.value !== null ? (
          <span className={cls(own.saidSoFar)}>{line.value}</span>
        ) : null}
        {/* Why there is no figure, in the body rather than the figure column. It is an
            explanation, and it belongs beside the requirement it explains — a sentence in a column
            sized for numbers squeezed the whole line to a word per row. */}
        {line.value === null && line.unavailable_because ? (
          <span className={cls(own.missing, isUnsupported && own.missingFact)}>
            {line.unavailable_because}
          </span>
        ) : null}
        {(isProse || line.measure === "intensity") && !isUnsupported ? (
          <StatementEditor
            line={line}
            regimeKey={regimeKey}
            obligationId={obligationId}
            method={method}
            filingWindow={filingWindow}
          />
        ) : null}
      </span>
      {/* The figure column is for figures. A narrative's "value" is a paragraph, and rendering it
          here — in a `white-space: nowrap` column sized to its content — collapsed the body to
          about forty pixels and wrapped every word onto its own line. Prose belongs in the body,
          beside the requirement it answers. */}
      {line.value !== null && !isProse ? (
        <span className={cls(own.figure)}>
          <span className={cls(own.value)} data-testid="report-line-value">{amount(line.value, line.unit)}</span>
          <span className={cls(own.unit)} data-testid="report-line-unit">{line.unit ?? ""}</span>
        </span>
      ) : line.value === null ? (
        /* **A row with no figure still says so in the figure column.** An unsupported line gets a
           row (an omission reads as nothing being required), and an empty right-hand cell reads
           as a layout gap rather than as a statement. */
        <span className={cls(own.figure)}>
          <span className={cls(own.noFigure, isUnsupported && own.noFigureUnsupported)}>
            {isUnsupported ? "Not supported" : isProse ? "Not written" : "No figure"}
          </span>
        </span>
      ) : null}
    </div>
  );
}
